import React, { useCallback, useEffect, useState } from 'react';
import { t } from '@citrite/translate';
import { notifyError, notifyInfo } from '@citrite/web-ui-component';
import { trackAnalyticsEvent } from 'analytics';
import { GenericErrorBoundary } from 'App/GenericErrorBoundary';
import {
	defaultContext,
	IServiceContinuityContext,
	ServiceContinuityContext,
} from 'App/ServiceContinuity/Context';
import { LeaseFile, LeaseFileType } from 'App/ServiceContinuity/types';
import { getShieldSupportabilityAnalyticsPayload as getAnalyticsPayload } from 'App/ServiceContinuity/utils';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { environment } from 'Environment';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { isNativeClient } from 'Environment/launchResource/device';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import {
	EventMode,
	shieldSupportabilityCASReporter,
} from 'Workspace/TelemetryEvents/shieldSupportability/createShieldSupportabilityCASReporter';

export let onLeaseSyncCompletedEvent = async (_syncStatus: string) => {};
const warningThreshold = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const _ServiceContinuityProvider: React.FC = ({ children }) => {
	const [serviceContinuityContext, setServiceContinuityContext] =
		useState<IServiceContinuityContext>(defaultContext);

	const { value: resourceContext } = useLoadableResourceContext();

	const updateContext = useCallback(
		(partialContext: Partial<IServiceContinuityContext>) => {
			setServiceContinuityContext(prevContext => ({
				...prevContext,
				...partialContext,
			}));
		},
		[]
	);

	const initializeLeaseSync = useCallback(async () => {
		trackAnalyticsEvent(
			shieldSupportabilityCASReporter.getManualNetworkResyncEvent(
				getAnalyticsPayload(serviceContinuityContext.shieldStatus)
			)
		);
		const leaseSyncResult = await environment.initLeaseSync();
		if (leaseSyncResult === 'Success') {
			updateContext({
				lastClientResyncTriggerTime: new Date(),
			});
		}
		return leaseSyncResult;
	}, [updateContext, serviceContinuityContext.shieldStatus]);

	const initializeLogCollectionCallback = useCallback(() => {
		trackAnalyticsEvent(
			shieldSupportabilityCASReporter.getCollectLogsEvent(
				getAnalyticsPayload(serviceContinuityContext.shieldStatus)
			)
		);
		environment.initLogCollection();
	}, [serviceContinuityContext.shieldStatus]);

	useEffect(() => {
		updateContext({
			initializeLeaseSync,
			initializeLogCollection:
				environment.supportsInitLogCollection && initializeLogCollectionCallback,
		});
	}, [initializeLeaseSync, initializeLogCollectionCallback, updateContext]);

	useEffect(() => {
		const expiredLeases: Resource[] = [];
		const expiringLeases: Resource[] = [];
		const activeLeases: Resource[] = [];
		const missingLeases: Resource[] = [];
		const leaseResourceFiles: LeaseFile[] = serviceContinuityContext?.leaseFiles.filter(
			({ document }) => document.manifest.leaseType === LeaseFileType.XD_Resource
		);
		resourceContext?.resources?.forEach(resource => {
			if (!!resource?.clmetadata) {
				const leaseFile = leaseResourceFiles.find(
					({ document }) =>
						resource?.clmetadata?.resourcekey === document.manifest.resourceKey
				);
				if (leaseFile) {
					const leaseValidTo = new Date(leaseFile?.document.manifest.validTo).getTime();
					const currentDate = new Date().getTime();
					if (leaseValidTo < currentDate) {
						expiredLeases.push(resource);
					} else if (leaseValidTo - currentDate <= warningThreshold) {
						expiringLeases.push(resource);
					} else {
						activeLeases.push(resource);
					}
				} else {
					missingLeases.push(resource);
				}
			}
		});

		updateContext({
			shieldStatus: {
				expiredLeases,
				expiringLeases,
				activeLeases,
				missingLeases,
				lastCLSyncStatus: serviceContinuityContext?.shieldInformation.lastCLSyncStatus,
				lastLeaseSyncSuccessTime:
					serviceContinuityContext?.shieldInformation.lastLeaseSyncSuccessTime,
			},
		});
	}, [
		updateContext,
		resourceContext?.resources,
		serviceContinuityContext?.shieldInformation,
		serviceContinuityContext?.leaseFiles,
	]);

	const syncPlatformLeaseInformation = useCallback(async () => {
		updateContext({ isLoading: true });
		const shieldInformation = await environment.getShieldInformation();
		const leaseFiles: LeaseFile[] = [];
		if (shieldInformation?.leaseFiles) {
			for (const filePath of shieldInformation.leaseFiles) {
				const leaseFile = await environment.getLocalFile<LeaseFile>(filePath);
				leaseFiles.push(leaseFile);
			}
		}
		updateContext({ shieldInformation, leaseFiles, isLoading: false });
	}, [updateContext]);

	useEffect(() => {
		trackAnalyticsEvent(
			shieldSupportabilityCASReporter.getLocalResyncAttemptEvent(
				getAnalyticsPayload(defaultContext.shieldStatus, EventMode.AUTO)
			)
		);
		syncPlatformLeaseInformation();
	}, [syncPlatformLeaseInformation]);

	useEffect(() => {
		updateContext({ syncPlatformLeaseInformation });
	}, [syncPlatformLeaseInformation, updateContext]);

	const onLeaseSyncCompleted = useCallback(
		async (syncStatus: string) => {
			if (
				!serviceContinuityContext.lastClientResyncTriggerTime &&
				!!serviceContinuityContext?.shieldInformation?.lastCLSyncStatus
			) {
				return;
			}
			if (syncStatus === 'Success') {
				trackAnalyticsEvent(
					shieldSupportabilityCASReporter.getLocalResyncAttemptEvent(
						getAnalyticsPayload(
							serviceContinuityContext.shieldStatus,
							EventMode.ON_SUCCESS_CWA_SYNC
						)
					)
				);
				await syncPlatformLeaseInformation();
				!!serviceContinuityContext.lastClientResyncTriggerTime &&
					notifyInfo(t('Workspace:service_continuity.sync_success'));
			} else {
				notifyError(t('Workspace:service_continuity.sync_failed'));
			}
			updateContext({ lastClientResyncTriggerTime: null });
		},
		[
			updateContext,
			syncPlatformLeaseInformation,
			serviceContinuityContext.lastClientResyncTriggerTime,
			serviceContinuityContext?.shieldInformation?.lastCLSyncStatus,
			serviceContinuityContext.shieldStatus,
		]
	);

	useEffect(() => {
		onLeaseSyncCompletedEvent = onLeaseSyncCompleted;
	}, [onLeaseSyncCompleted]);

	return (
		<ServiceContinuityContext.Provider value={serviceContinuityContext}>
			{children}
		</ServiceContinuityContext.Provider>
	);
};

const _ServiceContinuityProviderWrapper: React.FC = props => {
	const { featureCanaryConfiguration } = useConfigurationContext().configurationService;

	const isServiceContinuityEnabled = featureCanaryConfiguration.isFeatureEnabled(
		FeatureFlag.EnableServiceContinuitySupportability
	);
	const platformSupportsServiceContinuity =
		isNativeClient() && environment.shouldEnableServiceContinuitySupportability();

	return isServiceContinuityEnabled && platformSupportsServiceContinuity ? (
		<_ServiceContinuityProvider {...props} />
	) : (
		<>{props.children}</>
	);
};

export const ServiceContinuityProvider: React.FC = props => (
	<GenericErrorBoundary
		renderProp={() => <_ServiceContinuityProviderWrapper {...props} />}
		fallback={() => <>{props.children}</>}
		name="ServiceContinuityProvider"
	/>
);
